/**
 * Auth schemas — autenticazione Phase 3 (IMPL-P3-001).
 *
 * Schemi Zod condivisi tra API e Web per i flussi di autenticazione
 * (UC-005 registrazione, UC-006 verifica email, UC-007 login,
 * UC-008 reset password, UC-009 logout).
 *
 * Convenzioni:
 *  - Messaggi italiani col registro "tu" (coerenti con `user.ts`)
 *  - Stringhe trimmate dove sensato (delegato agli schemi base)
 *  - Tipi TypeScript esportati via `z.infer`
 */
import { z } from 'zod';
import { emailSchema, passwordSchema } from './user.js';
/**
 * Tipologia utente CLIENT (orthogonale al `Role`).
 * Specchia l'enum Prisma `TipologiaUtente`.
 */
export const tipologiaSchema = z.enum(['PRIVATO', 'AGENTE', 'COMMERCIALISTA'], {
    errorMap: () => ({ message: 'Seleziona la tipologia utente' }),
});
/**
 * Payload consensi GDPR raccolto al signup.
 * `tos` e `privacy` sono obbligatori (z.literal(true)); `marketing` è opt-in.
 * `version` è la versione del testo legale corrente (placeholder finché i
 * testi reali sono in revisione legale — vedi INTRODUCTION.md).
 */
export const consentPayloadSchema = z.object({
    tos: z.literal(true, {
        errorMap: () => ({ message: 'Devi accettare i Termini di Servizio' }),
    }),
    privacy: z.literal(true, {
        errorMap: () => ({ message: 'Devi accettare la Privacy Policy' }),
    }),
    marketing: z.boolean().default(false),
    version: z.string().min(1, { message: 'Versione consenso mancante' }),
});
/**
 * Input per UC-005 (signup).
 * `confirmPassword` è validato dal `.refine` finale.
 */
export const signupSchema = z
    .object({
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string(),
    tipologia: tipologiaSchema,
    consents: consentPayloadSchema,
})
    .refine((v) => v.password === v.confirmPassword, {
    message: 'Le password non coincidono',
    path: ['confirmPassword'],
});
/**
 * Input per UC-007 (login).
 * `password` è validata solo per non essere vuota — la policy completa
 * vale per il signup; al login accettiamo qualsiasi stringa per non leakare
 * informazioni sulla policy via differenti messaggi di errore.
 */
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, { message: 'Inserisci la password' }),
    rememberMe: z.boolean().optional().default(false),
});
/**
 * Input per UC-008 step 1 — richiesta reset password.
 */
export const forgotPasswordSchema = z.object({
    email: emailSchema,
});
/**
 * Input per UC-008 step 2 — submit nuovo password.
 */
export const resetPasswordSchema = z
    .object({
    token: z.string().min(1, { message: 'Token mancante' }),
    password: passwordSchema,
    confirmPassword: z.string(),
})
    .refine((v) => v.password === v.confirmPassword, {
    message: 'Le password non coincidono',
    path: ['confirmPassword'],
});
//# sourceMappingURL=auth.js.map